/**
 * A seção do exame à vista na aba Exame (E205.F2.T01–T02, ADR-0147 §2): uma seção por vez.
 *
 * - A seção vem do hash (`#documento-secao-*`): o índice, "Anterior"/"Próxima", o painel do dado e os
 *   links de fora (o próximo passo, "Resolver no documento") trocam a seção pelo endereço, e a mesa
 *   rola até ela (`useAbaDaMesa`). Sem seção no endereço, fica a última escolhida nesta saída, ou a
 *   primeira (o resumo executivo).
 * - O trecho aberto por fora da seção (`?trecho=`, a nota de uma seção) leva à seção dele.
 * - Trocar de seção com um trecho aberto em outra espera guardar o rascunho dele (E205.F2.C01,
 *   F-E205.F2-01): a seção do trecho continua à vista (o editor não desmonta com o texto) até a
 *   guarda imediata confirmar; aí o trecho fecha e a seção pedida aparece. Na recusa, o endereço
 *   volta para a seção do trecho, o texto e o estado de falha ficam no editor e a mesa diz que a
 *   seção não mudou. Pedidos durante a guarda não disparam outra: vale a última seção pedida.
 * - A guarda confirma a versão mais recente do texto (E205.F2.C02): o que a pessoa digita durante a
 *   espera vai numa guarda seguinte antes de o trecho fechar, e a recusa dela segura a seção com o
 *   texto novo. A resposta pertence ao trecho que a pediu: fechado ou trocado durante a espera, ela
 *   não mexe no que está aberto; de volta à seção dele, o trecho continua aberto.
 * - Os links mantêm a busca do endereço (`?saida=`, `?trecho=`) e trocam a entrada (`replace`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, type To } from 'react-router-dom';

import type { EdicaoNoDocumento } from '@/components/DocumentoDaMesa';
import type { SaidaDaEdicao } from '@/lib/correcaoDoRascunho';
import {
  secaoDoTrecho,
  type DocumentoDaSaidaData,
  type SecaoDoDocumentoData,
} from '@/lib/documentoDaSaida';

export interface SecaoDoExame {
  secoes: readonly SecaoDoDocumentoData[];
  /** A âncora da seção à vista; `null` sem documento. */
  ativa: string | null;
  linkDa: (ancora: string) => To;
  /** A troca pedida não aconteceu: o rascunho do trecho aberto não foi guardado. */
  naoTrocou: boolean;
}

const SEM_SECOES: readonly SecaoDoDocumentoData[] = [];

function useSecaoEscolhida(
  secoes: readonly SecaoDoDocumentoData[],
  saida: SaidaDaEdicao,
  ancora: string | null,
): string | null {
  const existe = (a: string | null): a is string =>
    a !== null && secoes.some((s) => s.ancora === a);
  const pedida = existe(ancora) ? ancora : null;
  const [ultima, setUltima] = useState<{ saida: SaidaDaEdicao; ancora: string } | null>(null);
  if (pedida !== null && (ultima?.ancora !== pedida || ultima.saida !== saida)) {
    setUltima({ saida, ancora: pedida });
  }
  const lembrada = ultima !== null && ultima.saida === saida ? ultima.ancora : null;
  return pedida ?? (existe(lembrada) ? lembrada : (secoes[0]?.ancora ?? null));
}

export function useSecaoDoExame(
  documento: DocumentoDaSaidaData | null,
  saida: SaidaDaEdicao,
  ancora: string | null,
  edicao: EdicaoNoDocumento | null,
): SecaoDoExame {
  const { search } = useLocation();
  const navigate = useNavigate();
  const secoes = documento?.aplicavel === true ? documento.secoes : SEM_SECOES;
  const pedida = useSecaoEscolhida(secoes, saida, ancora);
  const linkDa = useCallback((a: string): To => ({ search, hash: a }), [search]);

  const aberto = edicao?.aberto ?? null;
  const doAberto = aberto !== null ? secaoDoTrecho(documento, aberto) : null;
  // A seção do trecho aberto fica à vista enquanto ele está aberto: o editor não desmonta.
  const ativa = doAberto ?? pedida;
  const [naoTrocou, setNaoTrocou] = useState(false);
  const guardando = useRef(false);
  // Em ref: os efeitos abaixo reagem a UMA mudança cada (o trecho aberto; a seção pedida).
  const atual = useRef({ pedida, doAberto, edicao, linkDa });
  atual.current = { pedida, doAberto, edicao, linkDa };

  useEffect(() => {
    const { doAberto: secao, pedida: agora } = atual.current;
    if (secao !== null && secao !== agora) {
      navigate(atual.current.linkDa(secao), { replace: true });
    }
  }, [aberto, navigate]);

  useEffect(() => {
    const { doAberto: secao, edicao: ed } = atual.current;
    if (secao === null || secao === pedida || ed === null || guardando.current) return;
    // A sessão que pediu: a resposta só vale com este trecho ainda aberto.
    const trecho = ed.aberto;
    guardando.current = true;
    setNaoTrocou(false);
    ed.guardarJa().then(
      () => {
        guardando.current = false;
        const agora = atual.current;
        if (agora.edicao === null || agora.edicao.aberto !== trecho) return;
        // De volta à seção do trecho durante a espera: ele continua aberto.
        if (agora.pedida === secao) return;
        agora.edicao.fechar();
      },
      () => {
        guardando.current = false;
        if (atual.current.edicao?.aberto !== trecho) return;
        setNaoTrocou(true);
        navigate(atual.current.linkDa(secao), { replace: true });
      },
    );
  }, [pedida, navigate]);

  return { secoes, ativa, linkDa, naoTrocou: naoTrocou && aberto !== null };
}
